<template>
  <div class="page page-center">
    <!-- Top Bar with Logo and Download Button -->
    <div class="custom-top-bar">
      <div class="brand-area">
        <img :src="assets.logo" class="brand-logo" alt="logo" />
        <span class="brand-text">Kukukao</span>
      </div>
      <button class="btn-download" @click="handleOpenApp">下载app</button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <div class="loading-text">加载中...</div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <div class="error-text">{{ error }}</div>
      <button class="btn-retry" @click="fetchUserInfo">重试</button>
    </div>

    <!-- Main Content -->
    <div v-else class="phone">
      <div class="phone-inner">
        <!-- Profile Card -->
        <div class="section section-profile">
          <div class="profile-header">
             <div class="avatar-wrap">
                <img
                  v-if="userInfo.avatarUrl"
                  :src="userInfo.avatarUrl"
                  class="avatar-img"
                  alt="avatar"
                />
                <div v-else class="avatar-placeholder">头像</div>
             </div>
             <div class="user-info">
               <div class="user-name">{{ userInfo.userName || '用户' }}</div>
               <div class="user-id">ID:{{ userInfo.shortUserId || '--' }}</div>
             </div>
          </div>

          <div class="study-info-row">
            <div class="info-text">
              <div class="course-label">{{ userInfo.subjectName || '科目' }}学习中</div>
              <div class="course-value">{{ userInfo.stageName || '学习中' }}</div>
            </div>
            <img :src="assets.kuku" class="mascot-bird" alt="kuku" />
          </div>

          <div class="action-row">
            <button class="btn-add-friend" type="button" @click="handleOpenApp">
              <img :src="assets.add" class="add-btn-img" alt="添加好友" />
            </button>
          </div>
        </div>

        <!-- Stats Grid (4 items) -->
        <div class="section section-stats">
          <div class="stat-card">
            <img :src="assets.time" class="stat-icon" alt="" />
            <div class="stat-content">
              <div class="stat-val text-orange">
                {{ userInfo.totalLearnTime || '0' }}
              </div>
              <div class="stat-label">学习时长</div>
            </div>
          </div>
          <div class="stat-card">
            <img :src="assets.complate" class="stat-icon" alt="" />
            <div class="stat-content">
              <div class="stat-val text-red">
                {{ userInfo.totalLearnStageCount || 0 }}
              </div>
              <div class="stat-label">闯关次数</div>
            </div>
          </div>
          <div class="stat-card">
            <img :src="assets.spark" class="stat-icon" alt="" />
            <div class="stat-content">
               <div class="stat-val text-red">
                 {{ userInfo.streakSpark || 0 }}
               </div>
               <div class="stat-label">连续火花</div>
            </div>
          </div>
          <div class="stat-card">
             <img :src="assets.start" class="stat-icon" alt="" />
             <div class="stat-content">
                <div class="stat-val text-red user-exp-block">
                  <div class="sub-text">{{ userInfo.userGrade || '萌新' }}</div>
                  <div>{{ userInfo.totalExperience || 0 }}<span class="unit">总经验</span></div>
                </div>
             </div>
          </div>
        </div>

        <!-- Bottom Cards: Progress & Rank -->
        <div class="section section-bottom-cards">
           <div class="info-card">
             <img :src="assets.study" class="card-icon" alt="progress" />
             <div class="card-text">
               <div class="card-title text-blue">{{ userInfo.subjectName || '科目' }}</div>
               <div class="card-desc">课程进度 {{ formatProgress(userInfo.courseProgress) }}</div>
             </div>
           </div>
           <div class="info-card">
             <img :src="userInfo.userRankUrl || assets.stage" class="card-icon" alt="rank" />
             <div class="card-text">
               <div class="card-title text-gold">{{ userInfo.userRank || '段位' }}</div>
               <div class="card-desc">排行榜</div>
             </div>
           </div>
        </div>

      </div>
    </div>

    <!-- Bottom Floating Button Area -->
    <div class="bottom-area">
       <open-app-button text="打开app查看" @open-app="handleOpenApp" />
    </div>
  </div>
</template>

<script>
import OpenAppButton from '../../components/OpenAppButton.vue'
import { homeImages } from '../../homeImages'
import { openApp } from '@/utils/openApp'

// Assets
import logo from '@/assets/logo.svg'
import kuku from '@/assets/profile/kuku.svg'
import time from '@/assets/profile/time.svg'
import complate from '@/assets/profile/complate.svg'
import spark from '@/assets/profile/spark.svg'
import start from '@/assets/profile/start.svg'
import add from '@/assets/profile/add.svg'
import study from '@/assets/profile/study.svg'
import stage from '@/assets/profile/stage.svg'


export default {
  name: 'HomeLawMasterUserCenter',
  components: {
    OpenAppButton
  },
  props: {
    query: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      images: homeImages,
      assets: {
        logo,
        kuku,
        time,
        complate,
        spark,
        start,
        add,
        study,
        stage,
      },
      userInfo: {},
      loading: true,
      error: null
    }
  },
  computed: {
    userId() {
      // 从URL参数中获取id
      return this.query.id || (this.$route && this.$route.query && this.$route.query.id) || null
    }
  },
  mounted() {
    this.fetchUserInfo()
  },
  methods: {
    async fetchUserInfo() {
      if (!this.userId) {
        this.error = '缺少用户ID参数'
        this.loading = false
        return
      }

      this.loading = true
      this.error = null

      try {
        const response = await fetch(`${process.env.VUE_APP_API_BASE_URL || ''}/lawMaster/share/getUserInfo?userId=${this.userId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        })
        const result = await response.json()

        if (result.code === 200 && result.data) {
          this.userInfo = result.data
        } else {
          this.error = result.message || '获取用户信息失败'
        }
      } catch (err) {
        console.error('获取用户信息失败:', err)
        this.error = '网络错误，请稍后重试'
      } finally {
        this.loading = false
      }
    },
    formatProgress(progress) {
      if (!progress && progress !== 0) return ''
      return `${Math.round(progress * 100)}%`
    },
    handleOpenApp() {
      openApp()
    }
  }
}
</script>

<style lang="scss" scoped>
.page {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 80px;
  padding-top: calc(56px + env(safe-area-inset-top, 0px)); /* 为固定头部留出空间 */
}

// Custom Top Bar：固定顶部，不随页面滚动
.custom-top-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: calc(12px + env(safe-area-inset-top, 0px)) 20px 12px 20px;
  background: #fff;
}

.brand-area {
  display: flex;
  align-items: center;

  .brand-logo {
     width: 32px; height: 32px;
     margin-right: 8px;
  }
  .brand-text {
     font-size: 18px;
     font-weight: 700;
     color: #f15b5b; // Brand Color
  }
}

.btn-download {
  background: #f15b5b;
  color: #fff;
  border: none;
  border-radius: 99px;
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 600;
}


.phone {
  padding: 20px;
}

.phone-inner {
  border-radius: 20px;
  padding: 0;
}

.section {
  margin-bottom: 24px;
}

// Profile Section
.section-profile {
  background: #fff;
  border-radius: 20px;
  padding: 24px 20px 20px;
  position: relative;
  overflow: hidden;
}

.profile-header {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
}

.avatar-wrap {
  width: 60px; height: 60px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 4px 10px rgba(0,0,0,0.1);
  overflow: hidden;
  margin-right: 12px;
  background: #eee;

  .avatar-img { width: 100%; height: 100%; object-fit: cover; }
  .avatar-placeholder { width: 100%; height: 100%; display:flex; align-items:center; justify-content:center; color:#999; font-size:12px; }
}

.user-info {
  .user-name { font-size: 20px; font-weight: 800; color: #333; }
  .user-id { font-size: 13px; color: #999; margin-top: 4px; }
}

.study-info-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  position: relative;
  margin-bottom: 20px;

  .info-text {
     z-index: 2;
  }

  .course-label { font-size: 13px; color: #999; margin-bottom: 6px; }
  .course-value { font-size: 18px; font-weight: 700; color: #f15b5b; }

  .mascot-bird {
     position: absolute;
     right: 0;
     bottom: 0;
     width: 80px;
     height: auto;
  }
}

.action-row {
  border-top: 1px solid #f5f5f5;
  padding-top: 24px;
}

.btn-add-friend {
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  display: block;

  .add-btn-img {
    width: 100%;
    height: auto;
    display: block;
  }
}

// Stats Grid（间距统一 12px）
.section-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 12px;
  row-gap: 12px;
  margin-bottom: 12px;
}

.stat-card {
  background: #fff;
  border-radius: 16px;
  padding: 16px;
  display: flex;
  align-items: center;
}

.stat-icon {
  width: 36px; height: 36px;
  margin-right: 10px;
  flex-shrink: 0;
}

.stat-content {
  .stat-val {
    font-size: 18px; font-weight: 800; line-height: 1.2;
    .unit { font-size: 11px; font-weight: 400; color: #333; margin-left: 2px;}
    .sub-text { font-size: 13px; color: #ffca28; margin-right: 4px; }
  }
  .stat-label { font-size: 12px; color: #666; margin-top: 4px; }

  .text-orange { color: #feaa00; }
  .text-red { color: #f15b5b; }

  .user-exp-block {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    .sub-text {
       font-size: 13px;
       color: #ffca28;
       margin-bottom: 2px;
       margin-right: 0;
    }
  }
}

// Bottom Cards（间距统一 12px）
.section-bottom-cards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 12px;
  row-gap: 12px;
}

.info-card {
  background: #fff;
  border-radius: 16px;
  padding: 16px;
  display: flex;
  align-items: center;

  .card-icon { width: 32px; height: 32px; margin-right: 8px; }

  .card-text {
    .card-title { font-size: 14px; font-weight: 700; margin-bottom: 2px; }
    .card-desc { font-size: 11px; color: #999; }

    .text-blue { color: #42a5f5; }
    .text-gold { color: #ffa000; }
  }
}

.bottom-area {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #f5f5f5; // match bg
  padding: 12px 24px 24px;
}

// Loading State
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f0f0f0;
  border-top-color: #f15b5b;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-text {
  margin-top: 16px;
  font-size: 14px;
  color: #999;
}

// Error State
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
}

.error-text {
  font-size: 14px;
  color: #f15b5b;
  margin-bottom: 16px;
}

.btn-retry {
  background: #f15b5b;
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:active {
    opacity: 0.8;
  }
}
</style>
